/**
 * Generato automaticamente.
 * framework-cfg-utenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  DataOption,
  GetTipiWsEsterniParams,
  GetWsEsterniParams,
  TipoWsEsterno,
  WsEsterno,
  WsEsternoFilter,
  WsEsternoRequest,
  WsRispostaPaginataListWsEsterno,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Dettaglio del servizio esterno
 * @summary Dettaglio servizio esterno
 */
export const getWsEsterniId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsEsterno>(
    { url: `/framework-cfg-utenti/ws-esterni/${id}`, method: 'get', signal },
    options,
  );
};

export const getGetWsEsterniIdQueryKey = (id: string) =>
  [`/framework-cfg-utenti/ws-esterni/${id}`] as const;

export const getGetWsEsterniIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getWsEsterniId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getWsEsterniId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getWsEsterniId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetWsEsterniIdQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getWsEsterniId>>> = ({
    signal,
  }) => getWsEsterniId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetWsEsterniIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getWsEsterniId>>
>;
export type GetWsEsterniIdQueryError = ErrorType<unknown>;

/**
 * @summary Dettaglio servizio esterno
 */
export const useGetWsEsterniId = <
  TData = Awaited<ReturnType<typeof getWsEsterniId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getWsEsterniId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetWsEsterniIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Modifica un servizio esterno
 * @summary Modifica servizio esterno
 */
export const putWsEsterniId = (
  id: string,
  wsEsternoRequest: WsEsternoRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<WsEsterno>(
    {
      url: `/framework-cfg-utenti/ws-esterni/${id}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: wsEsternoRequest,
    },
    options,
  );
};

export const getPutWsEsterniIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putWsEsterniId>>,
    TError,
    { id: string; data: WsEsternoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putWsEsterniId>>,
  TError,
  { id: string; data: WsEsternoRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putWsEsterniId>>,
    { id: string; data: WsEsternoRequest }
  > = props => {
    const { id, data } = props ?? {};

    return putWsEsterniId(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutWsEsterniIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putWsEsterniId>>
>;
export type PutWsEsterniIdMutationBody = WsEsternoRequest;
export type PutWsEsterniIdMutationError = ErrorType<unknown>;

/**
 * @summary Modifica servizio esterno
 */
export const usePutWsEsterniId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putWsEsterniId>>,
    TError,
    { id: string; data: WsEsternoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutWsEsterniIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Cancella un servizio esterno
 * @summary Cancella servizio esterno
 */
export const deleteWsEsterniId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    { url: `/framework-cfg-utenti/ws-esterni/${id}`, method: 'delete' },
    options,
  );
};

export const getDeleteWsEsterniIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteWsEsterniId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteWsEsterniId>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteWsEsterniId>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteWsEsterniId(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteWsEsterniIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteWsEsterniId>>
>;

export type DeleteWsEsterniIdMutationError = ErrorType<unknown>;

/**
 * @summary Cancella servizio esterno
 */
export const useDeleteWsEsterniId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteWsEsterniId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getDeleteWsEsterniIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Cerca uno o più servizi esterni
 * @summary Cerca servizi esterni
 */
export const getWsEsterni = (
  params?: GetWsEsterniParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListWsEsterno>(
    { url: `/framework-cfg-utenti/ws-esterni`, method: 'get', params, signal },
    options,
  );
};

export const getGetWsEsterniQueryKey = (params?: GetWsEsterniParams) =>
  [`/framework-cfg-utenti/ws-esterni`, ...(params ? [params] : [])] as const;

export const getGetWsEsterniQueryOptions = <
  TData = Awaited<ReturnType<typeof getWsEsterni>>,
  TError = ErrorType<unknown>,
>(
  params?: GetWsEsterniParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getWsEsterni>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<Awaited<ReturnType<typeof getWsEsterni>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetWsEsterniQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getWsEsterni>>> = ({
    signal,
  }) => getWsEsterni(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetWsEsterniQueryResult = NonNullable<
  Awaited<ReturnType<typeof getWsEsterni>>
>;
export type GetWsEsterniQueryError = ErrorType<unknown>;

/**
 * @summary Cerca servizi esterni
 */
export const useGetWsEsterni = <
  TData = Awaited<ReturnType<typeof getWsEsterni>>,
  TError = ErrorType<unknown>,
>(
  params?: GetWsEsterniParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getWsEsterni>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetWsEsterniQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Inserisce un servizio esterno
 * @summary Inserisci servizio esterno
 */
export const postWsEsterni = (
  wsEsternoRequest: WsEsternoRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<WsEsterno>(
    {
      url: `/framework-cfg-utenti/ws-esterni`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: wsEsternoRequest,
    },
    options,
  );
};

export const getPostWsEsterniMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postWsEsterni>>,
    TError,
    { data: WsEsternoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postWsEsterni>>,
  TError,
  { data: WsEsternoRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postWsEsterni>>,
    { data: WsEsternoRequest }
  > = props => {
    const { data } = props ?? {};

    return postWsEsterni(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostWsEsterniMutationResult = NonNullable<
  Awaited<ReturnType<typeof postWsEsterni>>
>;
export type PostWsEsterniMutationBody = WsEsternoRequest;
export type PostWsEsterniMutationError = ErrorType<unknown>;

/**
 * @summary Inserisci servizio esterno
 */
export const usePostWsEsterni = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postWsEsterni>>,
    TError,
    { data: WsEsternoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostWsEsterniMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Effettua una ricerca con richieste multiple
 * @deprecated
 * @summary Effettua una ricerca con richieste multiple
 */
export const postWsEsterniMultiSearch = (
  wsEsternoFilter: WsEsternoFilter[],
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<WsEsterno[]>(
    {
      url: `/framework-cfg-utenti/ws-esterni/multi-search`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: wsEsternoFilter,
    },
    options,
  );
};

export const getPostWsEsterniMultiSearchMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postWsEsterniMultiSearch>>,
    TError,
    { data: WsEsternoFilter[] },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postWsEsterniMultiSearch>>,
  TError,
  { data: WsEsternoFilter[] },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postWsEsterniMultiSearch>>,
    { data: WsEsternoFilter[] }
  > = props => {
    const { data } = props ?? {};

    return postWsEsterniMultiSearch(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostWsEsterniMultiSearchMutationResult = NonNullable<
  Awaited<ReturnType<typeof postWsEsterniMultiSearch>>
>;
export type PostWsEsterniMultiSearchMutationBody = WsEsternoFilter[];
export type PostWsEsterniMultiSearchMutationError = ErrorType<unknown>;

/**
 * @deprecated
 * @summary Effettua una ricerca con richieste multiple
 */
export const usePostWsEsterniMultiSearch = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postWsEsterniMultiSearch>>,
    TError,
    { data: WsEsternoFilter[] },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostWsEsterniMultiSearchMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Importa nel database solo i wsEsterni inesistenti
 * @summary Importa nel database solo i wsEsterni inesistenti
 */
export const postWsEsterniImport = (
  wsEsterno: WsEsterno[],
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cfg-utenti/ws-esterni/import`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: wsEsterno,
    },
    options,
  );
};

export const getPostWsEsterniImportMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postWsEsterniImport>>,
    TError,
    { data: WsEsterno[] },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postWsEsterniImport>>,
  TError,
  { data: WsEsterno[] },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postWsEsterniImport>>,
    { data: WsEsterno[] }
  > = props => {
    const { data } = props ?? {};

    return postWsEsterniImport(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostWsEsterniImportMutationResult = NonNullable<
  Awaited<ReturnType<typeof postWsEsterniImport>>
>;
export type PostWsEsterniImportMutationBody = WsEsterno[];
export type PostWsEsterniImportMutationError = ErrorType<unknown>;

/**
 * @summary Importa nel database solo i wsEsterni inesistenti
 */
export const usePostWsEsterniImport = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postWsEsterniImport>>,
    TError,
    { data: WsEsterno[] },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostWsEsterniImportMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Restituisce tutti i tipi possibli dei servizi esterni o le info di un tipo se nella request è presente il suo id
 * @summary Cerca tipi dei wsEsterni
 */
export const getTipiWsEsterni = (
  params?: GetTipiWsEsterniParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<TipoWsEsterno[]>(
    {
      url: `/framework-cfg-utenti/tipi-ws-esterni`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetTipiWsEsterniQueryKey = (params?: GetTipiWsEsterniParams) =>
  [
    `/framework-cfg-utenti/tipi-ws-esterni`,
    ...(params ? [params] : []),
  ] as const;

export const getGetTipiWsEsterniQueryOptions = <
  TData = Awaited<ReturnType<typeof getTipiWsEsterni>>,
  TError = ErrorType<unknown>,
>(
  params?: GetTipiWsEsterniParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiWsEsterni>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTipiWsEsterni>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTipiWsEsterniQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTipiWsEsterni>>
  > = ({ signal }) => getTipiWsEsterni(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetTipiWsEsterniQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTipiWsEsterni>>
>;
export type GetTipiWsEsterniQueryError = ErrorType<unknown>;

/**
 * @summary Cerca tipi dei wsEsterni
 */
export const useGetTipiWsEsterni = <
  TData = Awaited<ReturnType<typeof getTipiWsEsterni>>,
  TError = ErrorType<unknown>,
>(
  params?: GetTipiWsEsterniParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiWsEsterni>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTipiWsEsterniQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Importa nel database i tipi dei wsEsterni
 * @summary Importa nel database i tipi dei wsEsterni
 */
export const postTipiWsEsterni = (
  tipoWsEsterno: TipoWsEsterno[],
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cfg-utenti/tipi-ws-esterni`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: tipoWsEsterno,
    },
    options,
  );
};

export const getPostTipiWsEsterniMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postTipiWsEsterni>>,
    TError,
    { data: TipoWsEsterno[] },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postTipiWsEsterni>>,
  TError,
  { data: TipoWsEsterno[] },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postTipiWsEsterni>>,
    { data: TipoWsEsterno[] }
  > = props => {
    const { data } = props ?? {};

    return postTipiWsEsterni(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostTipiWsEsterniMutationResult = NonNullable<
  Awaited<ReturnType<typeof postTipiWsEsterni>>
>;
export type PostTipiWsEsterniMutationBody = TipoWsEsterno[];
export type PostTipiWsEsterniMutationError = ErrorType<unknown>;

/**
 * @summary Importa nel database i tipi dei wsEsterni
 */
export const usePostTipiWsEsterni = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postTipiWsEsterni>>,
    TError,
    { data: TipoWsEsterno[] },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostTipiWsEsterniMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Restituisce gli id e i nomi dei servizi esterni filtrati per tipo di servizio esterno
 * @summary Cerca servizi esterni
 */
export const getWsEsterniOptionsTipoWsEsternoTipoWsEsternoId = (
  tipoWsEsternoId: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<DataOption[]>(
    {
      url: `/framework-cfg-utenti/ws-esterni/options/tipo-ws-esterno/${tipoWsEsternoId}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetWsEsterniOptionsTipoWsEsternoTipoWsEsternoIdQueryKey = (
  tipoWsEsternoId: string,
) =>
  [
    `/framework-cfg-utenti/ws-esterni/options/tipo-ws-esterno/${tipoWsEsternoId}`,
  ] as const;

export const getGetWsEsterniOptionsTipoWsEsternoTipoWsEsternoIdQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getWsEsterniOptionsTipoWsEsternoTipoWsEsternoId>
  >,
  TError = ErrorType<unknown>,
>(
  tipoWsEsternoId: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<
        ReturnType<typeof getWsEsterniOptionsTipoWsEsternoTipoWsEsternoId>
      >,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getWsEsterniOptionsTipoWsEsternoTipoWsEsternoId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetWsEsterniOptionsTipoWsEsternoTipoWsEsternoIdQueryKey(tipoWsEsternoId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getWsEsterniOptionsTipoWsEsternoTipoWsEsternoId>>
  > = ({ signal }) =>
    getWsEsterniOptionsTipoWsEsternoTipoWsEsternoId(
      tipoWsEsternoId,
      requestOptions,
      signal,
    );

  return { queryKey, queryFn, enabled: !!tipoWsEsternoId, ...queryOptions };
};

export type GetWsEsterniOptionsTipoWsEsternoTipoWsEsternoIdQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getWsEsterniOptionsTipoWsEsternoTipoWsEsternoId>>
  >;
export type GetWsEsterniOptionsTipoWsEsternoTipoWsEsternoIdQueryError =
  ErrorType<unknown>;

/**
 * @summary Cerca servizi esterni
 */
export const useGetWsEsterniOptionsTipoWsEsternoTipoWsEsternoId = <
  TData = Awaited<
    ReturnType<typeof getWsEsterniOptionsTipoWsEsternoTipoWsEsternoId>
  >,
  TError = ErrorType<unknown>,
>(
  tipoWsEsternoId: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<
        ReturnType<typeof getWsEsterniOptionsTipoWsEsternoTipoWsEsternoId>
      >,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetWsEsterniOptionsTipoWsEsternoTipoWsEsternoIdQueryOptions(
      tipoWsEsternoId,
      options,
    );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Restituisce tutti i wsEsterni
 * @summary Restituisce tutti i wsEsterni
 */
export const getTipiWsEsterniAll = (
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<TipoWsEsterno[]>(
    { url: `/framework-cfg-utenti/tipi-ws-esterni/all`, method: 'get', signal },
    options,
  );
};

export const getGetTipiWsEsterniAllQueryKey = () =>
  [`/framework-cfg-utenti/tipi-ws-esterni/all`] as const;

export const getGetTipiWsEsterniAllQueryOptions = <
  TData = Awaited<ReturnType<typeof getTipiWsEsterniAll>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getTipiWsEsterniAll>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryOptions<
  Awaited<ReturnType<typeof getTipiWsEsterniAll>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTipiWsEsterniAllQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTipiWsEsterniAll>>
  > = ({ signal }) => getTipiWsEsterniAll(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetTipiWsEsterniAllQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTipiWsEsterniAll>>
>;
export type GetTipiWsEsterniAllQueryError = ErrorType<unknown>;

/**
 * @summary Restituisce tutti i wsEsterni
 */
export const useGetTipiWsEsterniAll = <
  TData = Awaited<ReturnType<typeof getTipiWsEsterniAll>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getTipiWsEsterniAll>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTipiWsEsterniAllQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
